import type { ReactElement, ReactNode } from "react";
import type { TLShape, TLShapeId } from "@tldraw/tlschema";
import { FileHelpers } from "@tldraw/utils";
import { Box, type BoxModel } from "../geometry";
import type { ImageExportOptions } from "../editor";
import { CanvasEditorContext } from "../editor-context";
import type { CanvasEditor } from "./editor";

// SVG and image export. Each shape draws through its class's toSvg; shapes
// without one export their on-screen body inside a foreignObject, so export
// matches the canvas. Images rasterize through an SVG image on a canvas.

/** A font file the canvas uses, registered on screen and embedded in exports. */
export type CanvasFont = {
  family: string;
  src: string;
  weight?: string;
  style?: string;
  /** CSS unicode-range of a script subset, such as "U+0000-00FF,U+0131". */
  unicodeRange?: string;
};

const registered = new Set<string>();
/** Make the canvas fonts available to the page; each loads when text first uses it. */
export function registerFonts(fonts: CanvasFont[] = []) {
  if (typeof document === "undefined" || typeof FontFace === "undefined") return;
  for (const f of fonts) {
    const key = `${f.family}|${f.weight}|${f.style}|${f.src}`;
    if (registered.has(key)) continue;
    registered.add(key);
    document.fonts.add(
      new FontFace(f.family, `url(${JSON.stringify(f.src)})`, {
        weight: f.weight,
        style: f.style,
        unicodeRange: f.unicodeRange,
      }),
    );
  }
}

/** What shape classes receive when drawing themselves for export. */
export type SvgExportContext = {
  readonly isDarkMode: boolean;
  readonly scale: number;
  readonly pixelRatio: number;
  /** Shared definitions (filters, patterns), added once per key. */
  addExportDef(def: { key: string; getElement(): ReactElement | null | Promise<ReactElement | null> }): void;
  /** Exports wait for this work (such as loading an image) before finishing. */
  waitUntil(promise: Promise<unknown>): void;
};

const DEFAULT_PADDING = 32;
const MAX_SIDE = 4096;
const MAX_PIXELS = 16_000_000;
/** Room around an HTML body for strokes and labels that overflow its box. */
const HTML_OVERFLOW = 64;

/** Shapes to draw: the requested ones and everything inside them, in page order. */
function shapesToExport(editor: CanvasEditor, requested: TLShapeId[]) {
  const roots = new Set(requested.filter((id) => editor.getShape(id)));
  const out: TLShape[] = [];
  const visit = (parentId: string, inside: boolean) => {
    for (const id of editor.getSortedChildIdsForParent(parentId as never)) {
      const shape = editor.getShape(id);
      if (!shape || editor.isShapeHidden(shape)) continue;
      const included = inside || roots.has(id);
      if (included) out.push(shape);
      visit(id, included);
    }
  };
  visit(editor.getCurrentPageId(), false);
  return { shapes: out, roots: [...roots] };
}

/** Opacity including every ancestor's. */
function pageOpacity(editor: CanvasEditor, shape: TLShape) {
  let opacity = shape.opacity;
  for (let p = editor.getShape(shape.parentId); p; p = editor.getShape(p.parentId)) opacity *= p.opacity;
  return opacity;
}

/**
 * The page's style rules that apply to the exported markup, so HTML bodies
 * look as they do on screen (the canvas's own rules and the app's alike).
 */
function matchingCss(markup: string) {
  if (typeof document === "undefined" || typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(markup, "text/html");
  const matches = (selector: string) => {
    try {
      // Pseudo-elements never match an element; test the element they belong to.
      return !!doc.querySelector(selector.replace(/::?(before|after|placeholder|selection|marker)\b/g, ""));
    } catch {
      return false;
    }
  };
  const rules: string[] = [];
  for (const sheet of [...document.styleSheets]) {
    let list: CSSRuleList;
    try {
      list = sheet.cssRules;
    } catch {
      continue; // cross-origin stylesheet
    }
    for (const rule of [...list])
      if (rule instanceof CSSStyleRule && rule.selectorText.split(",").some((s) => matches(s.trim())))
        rules.push(rule.cssText);
  }
  return rules.join("\n");
}

const fontData = new Map<string, Promise<string>>();
/** @font-face rules, with the font bytes embedded, for families the drawing uses. */
/** Whether any character of the text falls in a CSS unicode-range. */
function coversAny(range: string, text: string) {
  const spans = range.split(",").map((part) => {
    const [from, to = from] = part.trim().replace(/^U\+/i, "").split("-");
    return [parseInt(from, 16), parseInt(to, 16)] as const;
  });
  for (const ch of text) {
    const code = ch.codePointAt(0)!;
    if (spans.some(([a, b]) => code >= a && code <= b)) return true;
  }
  return false;
}

async function fontCss(fonts: CanvasFont[], markup: string) {
  // Only the families the drawing names, and only subsets its text needs.
  const text = markup.replace(/<[^>]*>/g, "");
  const used = fonts.filter(
    (f) => markup.includes(f.family) && (!f.unicodeRange || coversAny(f.unicodeRange, text)),
  );
  const rules = await Promise.all(
    used.map(async (f) => {
      let data = fontData.get(f.src);
      if (!data) {
        data = fetch(f.src)
          .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`Font ${f.family} unavailable`))))
          .then((b) => FileHelpers.blobToDataUrl(b));
        fontData.set(f.src, data);
        data.catch(() => fontData.delete(f.src));
      }
      try {
        const range = f.unicodeRange ? `unicode-range:${f.unicodeRange};` : "";
        return `@font-face{font-family:"${f.family}";src:url(${await data});font-weight:${f.weight ?? "normal"};font-style:${f.style ?? "normal"};${range}}`;
      } catch {
        return ""; // a missing font falls back rather than failing the export
      }
    }),
  );
  return rules.join("\n");
}

const escapeCss = (css: string) => css.replaceAll("<", "\\3C ");

/** Draw shapes as a standalone SVG document. */
export async function exportSvg(
  editor: CanvasEditor,
  ids: TLShapeId[],
  opts: ImageExportOptions & { fonts?: CanvasFont[] } = {},
): Promise<{ svg: string; width: number; height: number } | undefined> {
  const { shapes, roots } = shapesToExport(editor, ids);
  if (!shapes.length) return undefined;
  const area: BoxModel | undefined =
    opts.bounds ?? Box.Common(roots.map((id) => editor.getShapePageBounds(id)).filter((b): b is Box => !!b));
  if (!area) return undefined;
  const padding = typeof opts.padding === "number" ? opts.padding : DEFAULT_PADDING;
  const box = Box.From(area).expandBy(padding);

  const defs = new Map<string, ReactElement | null>();
  const pending: Promise<unknown>[] = [];
  const ctx: SvgExportContext = {
    isDarkMode: false,
    scale: opts.scale ?? 1,
    pixelRatio: opts.pixelRatio ?? 1,
    addExportDef(def) {
      if (defs.has(def.key)) return;
      defs.set(def.key, null);
      pending.push(Promise.resolve(def.getElement()).then((el) => void defs.set(def.key, el)));
    },
    waitUntil(promise) {
      pending.push(promise);
    },
  };

  const clips: ReactNode[] = [];
  const bodies = await Promise.all(
    shapes.map(async (shape, i) => {
      const util = editor.getUtil(shape) as {
        toSvg?(shape: TLShape, ctx: SvgExportContext): ReactNode | Promise<ReactNode>;
        component(shape: TLShape): ReactNode;
      };
      let body: ReactNode;
      if (util.toSvg) {
        body = await util.toSvg(shape, ctx);
        if (body == null) return null;
      } else {
        // On-screen body inside a foreignObject, with room for overflow.
        const b = editor.getShapeGeometry(shape).bounds;
        const x = Math.min(0, b.minX) - HTML_OVERFLOW,
          y = Math.min(0, b.minY) - HTML_OVERFLOW;
        const w = Math.max(1, b.maxX) - x + HTML_OVERFLOW,
          h = Math.max(1, b.maxY) - y + HTML_OVERFLOW;
        body = (
          <foreignObject x={x} y={y} width={w} height={h}>
            <div
              {...{ xmlns: "http://www.w3.org/1999/xhtml" }}
              className="canvas-shape"
              style={{ position: "absolute", left: -x, top: -y, width: Math.max(1, b.maxX), height: Math.max(1, b.maxY) }}
            >
              {util.component(shape)}
            </div>
          </foreignObject>
        );
      }
      const m = editor.getShapePageTransform(shape);
      const polygon = editor.getShapeClipPolygon(shape.id);
      const clipId = polygon ? `export-clip-${i}` : undefined;
      if (polygon && clipId)
        clips.push(
          <clipPath key={clipId} id={clipId} clipPathUnits="userSpaceOnUse">
            <polygon points={polygon.map((p) => `${p.x},${p.y}`).join(" ")} />
          </clipPath>,
        );
      return (
        <g key={shape.id} clipPath={clipId ? `url(#${clipId})` : undefined} opacity={pageOpacity(editor, shape)}>
          <g transform={`matrix(${m.a} ${m.b} ${m.c} ${m.d} ${m.e} ${m.f})`}>{body}</g>
        </g>
      );
    }),
  );
  await Promise.all(pending);

  const background = (editor.getCurrentTheme() as unknown as { colors: { light: { background: string } } }).colors.light
    .background;
  // Loaded on first export: React's server renderer is only needed here.
  const { renderToStaticMarkup } = await import("react-dom/server");
  const markup = renderToStaticMarkup(
    <CanvasEditorContext.Provider value={editor}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        {...{ "xmlns:xlink": "http://www.w3.org/1999/xlink" }}
        width={box.w}
        height={box.h}
        viewBox={`${box.minX} ${box.minY} ${box.w} ${box.h}`}
        style={{ backgroundColor: opts.background ? background : "transparent" }}
      >
        <defs>
          {[...defs.entries()].map(([key, el]) => el && <g key={key}>{el}</g>)}
          {clips}
        </defs>
        {opts.background && <rect x={box.minX} y={box.minY} width={box.w} height={box.h} fill={background} />}
        {bodies}
      </svg>
    </CanvasEditorContext.Provider>,
  );
  const css = escapeCss(`${matchingCss(markup)}\n${await fontCss(opts.fonts ?? [], markup)}`);
  const svg = markup
    .replace("<defs>", `<defs><style>${css}</style>`)
    // SVG inside an HTML body (foreignObject) needs its namespace stated, or
    // the standalone document treats it as an unknown HTML element.
    .replace(/<svg(?![^>]*\bxmlns=)/g, '<svg xmlns="http://www.w3.org/2000/svg"');
  return { svg, width: box.w, height: box.h };
}

const SETTLE_SIDE = 512;
const SETTLE_INTERVAL_MS = 50;
const SETTLE_QUIET = 2;
/** Chrome shows fallback text once a font's 3s block period ends. */
const SETTLE_LIMIT_MS = 3000;
/**
 * An SVG image lays out on its first paint, not on decode, and only then starts
 * loading its embedded fonts. Text waiting on a font paints blank, and every
 * run at the same size and weight waits with it (a ₹ that needs another script
 * subset blanked all regular 16px text). Paint small copies until they stop
 * changing.
 */
async function settleFonts(image: HTMLImageElement, width: number, height: number) {
  const fit = Math.min(1, SETTLE_SIDE / Math.max(width, height));
  const probe = document.createElement("canvas");
  probe.width = Math.max(1, Math.round(width * fit));
  probe.height = Math.max(1, Math.round(height * fit));
  const context = probe.getContext("2d", { willReadFrequently: true });
  if (!context) return;
  const paint = () => {
    context.clearRect(0, 0, probe.width, probe.height);
    context.drawImage(image, 0, 0, probe.width, probe.height);
    return context.getImageData(0, 0, probe.width, probe.height).data;
  };
  const same = (a: Uint8ClampedArray, b: Uint8ClampedArray) => a.every((v, i) => v === b[i]);
  const start = performance.now();
  let last = paint(),
    quiet = 0;
  while (quiet < SETTLE_QUIET && performance.now() - start < SETTLE_LIMIT_MS) {
    await new Promise((resolve) => setTimeout(resolve, SETTLE_INTERVAL_MS));
    const next = paint();
    quiet = same(last, next) ? quiet + 1 : 0;
    last = next;
  }
  probe.width = 0;
  probe.height = 0;
}

/** Rasterize an SVG document, within the canvas size limits. */
export async function rasterize(
  svg: string,
  width: number,
  height: number,
  opts: { scale?: number; pixelRatio?: number; format?: "png" | "jpeg" | "webp"; quality?: number },
) {
  const requested = (opts.scale ?? 1) * (opts.pixelRatio ?? 1);
  const fit = Math.min(1, MAX_SIDE / (Math.max(width, height) * requested), Math.sqrt(MAX_PIXELS / (width * height * requested ** 2)));
  const w = Math.max(1, Math.floor(width * requested * fit)),
    h = Math.max(1, Math.floor(height * requested * fit));
  const image = new Image();
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await image.decode();
  await settleFonts(image, width, height);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  try {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not create an image of these objects.");
    context.drawImage(image, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, `image/${opts.format ?? "png"}`, opts.quality),
    );
    if (!blob) throw new Error("Could not create an image of these objects.");
    return { blob, width: w, height: h };
  } finally {
    // Release the backing store straight away; exports can be large.
    canvas.width = 0;
    canvas.height = 0;
  }
}
